/*
 * The tempo every vignette plays at: one knob over the story engine's clock.
 *
 * A script (a unit's beats, a bento vignette's holds, a typing run) is
 * written in script ms; it plays at TEMPO of that. At 0.75 a beat written to
 * fire 1200ms after the one before fires 900ms after it, and a character
 * typed every 60ms is typed every 45ms. chain() paces a story's timeline;
 * pacedTimeline() a vignette's own holds; the story pointer's and the
 * cameras' glides read the same knob (cursorGlideMs, cameraGlideS,
 * GLIDE_S), so a glide still lands before the beat that waits on it.
 *
 * What a beat sets off as the app's own feedback (a dialog, the value
 * drawer, a fade, a row rising, a chart drawing) keeps its duration: the
 * knob moves the story along, it does not hurry the app. A beat that waits
 * on such feedback says so (`settles`, from SETTLES below), and the knob
 * never starts the next beat before it is over. Under reduced motion
 * nothing plays (every vignette rests on its end state), so the knob has
 * nothing to change there.
 */

/** The share of its script's time a vignette plays in. */
export const TEMPO = 0.75;

/**
 * How fast the vignettes type, in script ms a character (played at TEMPO, a
 * number at about 22 characters a second, a sentence at about 30): a short
 * number every 60 (45ms played), a sentence every 45 (34ms played).
 */
export const TYPE_NUMBER_MS = 60;
export const TYPE_SENTENCE_MS = 45;

/** `ms` of script time, played at `tempo` (whole ms). */
export function paced(ms, tempo = TEMPO) {
    return Math.round(ms * tempo);
}

/**
 * A vignette's holds (usePlayback's `timeline`), written in script ms,
 * played at `tempo`. `floors` (played ms, by step) are holds the knob may
 * not shorten below: an app animation the step waits on.
 */
export function pacedTimeline(holds, tempo = TEMPO, floors = []) {
    return Object.freeze(holds.map((ms, step) => Math.max(paced(ms, tempo), floors[step] ?? 0)));
}

/**
 * How long a finished vignette rests on its end state, once that state has
 * settled, before it dips back to its opening and plays again (played ms,
 * not paced). A vignette whose last step sets off a longer animation rests
 * that much longer (its `settles`), so the end is still for this long.
 */
export const REST_MS = 1200;

/**
 * How long the public viewer's bar chart takes to draw its bars once it is
 * shown: the app's BarChart (Components/charts/bar-chart.jsx, at its default
 * duration) grows each bar over 1100ms, starts them one after another over
 * 40% of that, and counts itself drawn 1540ms in (its `ready` phase; a test
 * holds this equal to it). A story that ends on it rests that much longer.
 */
export const CHART_DRAW_MS = 1540;

/**
 * How long the app's own feedback takes that a beat sets off and the beat
 * after it waits on: played ms, for a beat's `settles` (see story.js), so
 * the knob never has the next beat start before it is over. The modules
 * that animate each own the number; a test holds every one equal to it.
 */
export const SETTLES = Object.freeze({
    /** The pointer's press (StoryCursor's PRESS_MS). */
    press: 240,
    /** The value drawer rising, and falling (ValueDrawer's SHEET_IN_MS and SHEET_OUT_MS). */
    drawerIn: 450,
    drawerOut: 300,
    /** A window navigation: the page left is dropped at its fade's visual end (useDip's DIP_SWAP_MS), the next comes in (StoryVignette's PAGE_IN_MS). */
    page: 315,
    /** The lock banner fading in on a release (PassportCues' BANNER_IN_MS). */
    banner: 300,
    /** The public page taking the brand colour (PublicPassportVignette's BRAND_COLOUR_MS). */
    brand: 500,
    /** The public viewer's bar chart drawing. */
    chart: CHART_DRAW_MS,
});
